import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Avatar,
    CircularProgress,
    Divider,
} from '@mui/material';
import { MdAccountBalanceWallet } from 'react-icons/md';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { closeModal, showSnackbar } from '../../../redux/slice/AppSlice';
import api from '../../../config/interceptor';

const StripePayoutModal = ({ user, onSuccess = () => {}, payoutMethod={} }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);

    const availableBalance = parseFloat(user?.available_balance || 0);
    const stripeColor = theme.palette.primary.main;

    // Handle Stripe payout
    const handleStripePayout = async () => {
        if (availableBalance <= 0) {
            dispatch(showSnackbar({
                message: 'No available balance to payout',
                severity: 'error',
            }));
            return;
        }

        setLoading(true);

        try {
            // ✅ Call Stripe payout endpoint 
            const response = await api.post('/payout/stripe/send', {
                artist_id: user.id,
                amount: availableBalance,
                currency: 'usd',
            });

            if (response.data?.isSuccess) {
                dispatch(closeModal());
                dispatch(showSnackbar({
                    message: 'Payout initiated via Stripe successfully. Funds will arrive in 2-3 business days.',
                    severity: 'success',
                }));
                onSuccess(); // Refresh data
            } else {
                const errorMsg = response.data?.message?.msg || 'Failed to process Stripe payout';
                dispatch(showSnackbar({
                    message: errorMsg,
                    severity: 'error',
                }));
            }

        } catch (error) {
            console.error('Stripe payout error:', error);
            
            let errorMessage = 'Failed to process Stripe payout';
            
            if (error.response?.data) {
                const data = error.response.data;
                if (data.message?.msg) {
                    errorMessage = data.message.msg;
                } else if (data.message?.code) {
                    switch (data.message.code) {
                        case 'STRIPE_NOT_CONNECTED':
                            errorMessage = 'Artist has not connected their Stripe account';
                            break;
                        case 'INSUFFICIENT_BALANCE':
                            errorMessage = 'Insufficient balance for payout';
                            break;
                        case 'STRIPE_ERROR':
                            errorMessage = 'Stripe encountered an error. Please try again.';
                            break;
                        default:
                            errorMessage = data.message.msg || 'An error occurred';
                    }
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            dispatch(showSnackbar({
                message: errorMessage,
                severity: 'error',
            }));

        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ width: '100%', minWidth: '400px', maxWidth: '450px' }}>
            {/* Header */}
            <Typography
                variant="h6"
                sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.text.primary,
                    mb: 1,
                }}
            >
                Process Payout
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mb: 3 }}
            >
                {user?.first_name} {user?.last_name} - ${availableBalance.toFixed(2)} available
            </Typography>

            {/* User Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar
                    src={user?.profile_picture || ''}
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.blackColor.black1,
                        fontSize: '1rem',
                        fontWeight: 700,
                    }}
                >
                    {user?.first_name?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            fontSize: '1rem',
                        }}
                    >
                        {user?.first_name} {user?.last_name}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        {user?.email}
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

            {/* Payment Method Badge */}
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 2,
                    py: 0.75,
                    borderRadius: 2,
                    backgroundColor: `${stripeColor}15`,
                    border: `1px solid ${stripeColor}40`,
                    mb: 3,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ 
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                        mr: 1,
                    }}
                >
                    Payout Method
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ 
                        color: stripeColor,
                        fontWeight: 700,
                    }}
                >
                    Stripe
                </Typography>
            </Box>

            {/* Automatic Payout Information */}
            <Box
                sx={{
                    backgroundColor: `${stripeColor}08`,
                    border: `1px solid ${stripeColor}30`,
                    borderRadius: 2,
                    p: 2.5,
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <MdAccountBalanceWallet size={24} color={stripeColor} />
                    <Typography
                        variant="body1"
                        sx={{ 
                            color: theme.palette.text.primary,
                            fontWeight: 700,
                        }}
                    >
                        Automatic Payout
                    </Typography>
                </Box>
                <Typography
                    variant="body2"
                    sx={{ 
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                        mb: 2,
                    }}
                >
                    This artist uses Stripe Connect. Clicking "Send Payment via Stripe" will initiate the payout through Stripe. The artist will receive their funds within 2-3 business days.
                </Typography>

                {/* Amount Badge */}
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 2.5,
                        py: 1.25,
                        borderRadius: 2,
                        backgroundColor: theme.palette.background.paper,
                        border: `1.5px solid ${stripeColor}`,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{ 
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            mr: 1,
                        }}
                    >
                        Payout Amount:
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{ 
                            color: stripeColor,
                            fontWeight: 800,
                        }}
                    >
                        ${availableBalance.toFixed(2)}
                    </Typography>
                </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: "column" , gap: 2 }}>

                {/* Warning if payout is already in progress / paid */}
                {payoutMethod?.is_ready_for_withdrawal === false && (
                    <Box
                        sx={{
                            backgroundColor: 'rgba(255, 152, 0, 0.12)',
                            borderLeft: '4px solid #FF9800',
                            borderRadius: 1,
                            p: 2,
                            mb: 3,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#FF9800', fontWeight: 600 }}>
                            ⚠️ Waiting for Stripe to process the previous payout
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
                            The "Send Payment via Stripe" button is disabled for this artist until the current transfer is completed.
                        </Typography>
                    </Box>
                )}

                {/* Sumbit */}
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleStripePayout}
                    disabled={
                        loading || 
                        availableBalance <= 0 || 
                        payoutMethod?.is_ready_for_withdrawal === false   // ← New condition
                    }
                    startIcon={<MdAccountBalanceWallet />}
                    sx={{
                        backgroundColor: theme?.palette?.text?.secondary,
                        color: theme?.palette?.primary?.contrastText,
                        borderRadius: 2,
                        py: 1.3,
                        fontWeight: 700,
                        '&:hover': {
                            backgroundColor: theme.palette.text.secondary,
                            opacity: 0.9,
                        },
                        '&:disabled': {
                            backgroundColor: `${theme.palette.text.secondary}`,
                            color: 'rgba(255,255,255,0.4)',
                        },
                    }}
                >
                    {loading ? (
                        <CircularProgress size={20} sx={{ color: '#fff' }} />
                    ) : (
                        'Send Payment via Stripe'
                    )}
                </Button>

                <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => dispatch(closeModal())}
                    disabled={loading}
                    sx={{
                        borderColor: 'rgba(255,255,255,0.2)',
                        color: theme.palette.text.primary,
                        borderRadius: 2,
                        py: 1.3,
                        fontWeight: 600,
                        '&:hover': {
                            borderColor: 'rgba(255,255,255,0.4)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                    }}
                >
                    Cancel
                </Button>
            </Box>
        </Box>
    );
};

export default StripePayoutModal;